import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Popover,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Switch,
  InputAdornment,
  Divider,
  FormControlLabel,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  useTSQPerson,
  useTSMCreatePerson,
  useTSMUpdatePerson,
} from "../../hooks/useTSEntities";
import {
  EmployeeFull,
  EmployeeCreate,
  EmployeeUpdate,
  PersonType,
} from "../../types/personTypes";

import stringUtils from "../../utils/stringUtils";

// Validation schema
const employeeSchema = yup.object({
  first_name: yup.string().required("First Name is required."),
  last_name: yup.string().required("Last Name is required."),
});

interface EmployeeFormData {
  first_name: string;
  last_name: string;
  person_type: PersonType;
  title?: string;
  job_title?: string;
  email: string;
  phone?: string;
  notes?: string;
  user?: {};
  slack_id?: string;
  sales_rep: boolean;
}

export const PageEmployeeForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Mode detection from URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");
  const formMode = searchParams.get("mode") || "create";
  const refFormMode = useRef<string>(formMode);
  if (!formMode) {
    throw new Error("MISSING FORM MODE");
  }
  const refRenderCount = useRef<number>(1);
  const refFirstRender = useRef<boolean>(true);

  // State
  const [error, setError] = useState<string | null>(null);

  const createEmployeeMutation = useTSMCreatePerson();
  const updateEmployeeMutation = useTSMUpdatePerson();

  const {
    data: thisEmpData,
    isLoading: thisEmpLoading,
    error: thisEmpError,
    refetch: thisEmpRefetch,
  } = useTSQPerson(id || "", "employee");

  const refOrigData = useRef<EmployeeFull | null>(null);

  const refFormState = useRef<boolean>(false);

  const formLoading = thisEmpLoading;

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<EmployeeFormData>({
    resolver: yupResolver(employeeSchema) as any,
    defaultValues: {
      first_name: "",
      last_name: "",
      title: undefined,
      job_title: undefined,
      person_type: "employee",
      email: "",
      phone: undefined,
      notes: undefined,
      user: undefined,
      slack_id: undefined,
      sales_rep: false,
    },
  });

  useEffect(() => {
    refFirstRender.current = refRenderCount.current === 1;
    console.log(
      `Render #${refRenderCount.current}`,
      refFirstRender.current,
      refFormMode.current
    );
  });

  useEffect(() => {
    if (thisEmpData && formMode !== "create") {
      console.log("FETCHED DATA", thisEmpData);
      reset({
        first_name: thisEmpData.first_name,
        last_name: thisEmpData.last_name,
        title: thisEmpData.title,
        person_type: thisEmpData.person_type,
        job_title: thisEmpData.job_title,
        sales_rep:
          "sales_rep" in thisEmpData ? thisEmpData.sales_rep : undefined,
        phone: thisEmpData.phone,
        email: thisEmpData.email,
        notes: thisEmpData.notes,
        slack_id: "slack_id" in thisEmpData ? thisEmpData.slack_id : undefined,
        user: "user" in thisEmpData ? thisEmpData.user : undefined,
      });
      refOrigData.current = thisEmpData as EmployeeFull;
    }
    refFormState.current = true;
  }, [thisEmpData, formMode, reset]);

  // Form Functions
  // Mode switching functions
  const switchToViewMode = () => {
    navigate(`/people/form?mode=view&id=${id}&type=employee`);
    thisEmpRefetch();
  };

  const switchToEditMode = () => {
    navigate(`/people/form?mode=edit&id=${id}&type=employee`);
    thisEmpRefetch();
  };

  const switchToCreateMode = () => {
    navigate("/people/form?mode=create&type=employee");
  };

  // Form submission
  const onSubmit = async (data: EmployeeFormData) => {
    try {
      setError(null);

      if (formMode === "create") {
        const empPayload: EmployeeCreate = {
          first_name: data.first_name,
          last_name: data.last_name,
          title: data.title,
          job_title: data.job_title,
          phone: data.phone,
          email: data.email,
          person_type: "employee",
          sales_rep: data.sales_rep,
          slack_id: data.slack_id,
          notes: data.notes,
          new_addresses: [],
        };
        const new_ent = await createEmployeeMutation.mutateAsync(empPayload);
        navigate(`/people/form?mode=view&id=${new_ent.id}&type=employee`);
      } else if (formMode === "edit") {
        if (!id) {
          throw new Error("Lot ID is required for updates");
        }
        let origData = refOrigData.current as EmployeeFull;
        const empPayload: EmployeeUpdate = {
          first_name:
            data.first_name !== origData.first_name
              ? data.first_name
              : undefined,
          last_name:
            data.last_name !== origData.last_name ? data.last_name : undefined,
          person_type: "employee",
          sales_rep:
            data.sales_rep !== origData.sales_rep ? data.sales_rep : undefined,
          notes: data.notes,
          slack_id: data.slack_id,
          title: data.title,
          job_title: data.job_title,
          phone: data.phone,
          email: data.email,
          new_addresses:[],
          edit_addresses:[],
          delete_addresses:[]
        };
        await updateEmployeeMutation.mutateAsync({
          id: id,
          data: empPayload,
        });
        navigate(`/people/form?mode=view&id=${id}&type=employee`);
      }
    } catch (err: any) {
      console.error("Failed to save company:", err);
      setError(
        err.response?.data?.detail || err.message || "Failed to save company"
      );
    }
  };

  const handleCancel = () => {
    navigate(`/people?type=employee`);
  };

  if (formLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          variant="outlined"
        >
          {`Back to Employees`}
        </Button>
        <Typography variant="h4" component="h1">
          {formMode === "create"
            ? `Create Employee`
            : formMode === "view"
            ? `View Employee: ${thisEmpData?.first_name || ""}`
            : `Edit Employee: ${thisEmpData?.first_name || ""}`}
        </Typography>
        {formMode === "view" && (
          <Button
            variant="outlined"
            onClick={switchToCreateMode}
            startIcon={<AddIcon />}
          >
            Create New
          </Button>
        )}
        {formMode === "view" && (
          <Button
            variant="outlined"
            onClick={switchToEditMode}
            startIcon={<EditIcon />}
          >
            Edit
          </Button>
        )}
        {formMode === "edit" && (
          <Button
            variant="outlined"
            onClick={switchToViewMode}
            startIcon={<VisibilityIcon />}
          >
            View
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={() => {
            console.log(getValues());
          }}
        >
          DEBUG PRINT
        </Button>
      </Stack>

      {/* Error Alert */}
      {(error || thisEmpError) && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error || thisEmpError?.message || "An unknown error occurred"}
        </Alert>
      )}

      {/* Form */}
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit(onSubmit, console.error)}>
          <Stack spacing={3}>
            {/* Basic Information */}
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Title"
                    fullWidth
                    error={!!errors.title}
                    helperText={errors.title?.message || ""}
                    disabled={formMode === "view"}
                  />
                )}
              />
              <Controller
                name="first_name"
                control={control}
                rules={{
                  required: "First Name is required.",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name"
                    fullWidth
                    error={!!errors.first_name}
                    helperText={errors.first_name?.message || ""}
                    disabled={formMode === "view"}
                  />
                )}
              />
              <Controller
                name="last_name"
                control={control}
                rules={{
                  required: "Last Name is required.",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    fullWidth
                    error={!!errors.last_name}
                    helperText={errors.last_name?.message || ""}
                    disabled={formMode === "view"}
                  />
                )}
              />
            </Box>

            <Divider />

            {/* Quantities and Cost */}
            <Typography variant="h6" gutterBottom>
              Details
            </Typography>

            <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
              <Stack direction="column" gap={2} width="50%">
                <Box display="flex" gap={2}>
                  <Controller
                    name="job_title"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Job Title"
                        fullWidth
                        error={!!errors.job_title}
                        helperText={errors.job_title?.message || ""}
                        disabled={formMode === "view"}
                      />
                    )}
                  />
                  <Controller
                    name="sales_rep"
                    control={control}
                    render={({ field: { onChange, value, ...restField } }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            {...restField}
                            checked={value}
                            onChange={(event) => onChange(event.target.value)}
                            disabled={formMode === "view"}
                          />
                        }
                        label="Sales Rep?"
                      />
                    )}
                  />
                </Box>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email"
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email?.message || ""}
                      disabled={formMode === "view"}
                    />
                  )}
                />
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Phone"
                      fullWidth
                      error={!!errors.phone}
                      helperText={errors.phone?.message || ""}
                      disabled={formMode === "view"}
                    />
                  )}
                />
                <Controller
                  name="slack_id"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Slack ID"
                      fullWidth
                      error={!!errors.slack_id}
                      helperText={errors.slack_id?.message || ""}
                      disabled={formMode === "view"}
                    />
                  )}
                />
              </Stack>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notes"
                    multiline
                    rows={8}
                    fullWidth
                    disabled={formMode === "view"}
                    error={!!errors.notes}
                    helperText={
                      errors.notes?.message ||
                      `Optional notes about this Employee`
                    }
                  />
                )}
              />
            </Box>

            {/* Form Actions */}
            {!(formMode === "view") && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                  pt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  startIcon={<CancelIcon />}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Saving..."
                    : formMode === "create"
                    ? `Create Employee`
                    : `Update Employee`}
                </Button>
              </Box>
            )}
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};
